import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useGameStore } from '@/store/gameStore'
import { useAchievementsStore } from '@/store/achievementsStore'
import { WEEKLY_GOALS, MILESTONE_GOALS, Goal } from '@/config/sponsorshipConfig'
import { getGoalProgress, countClaimableGoals, getFirstClaimableGoalId } from '../helpers/goals'
import { getNextLevel } from '@/config/clubLevelConfig'
import { SectionDeck, IconTile, EmptyStateRich } from '../components/CourtVision'
import { AngledButton, BroadcastHeader } from '../components/broadcast'
import { GameIcon } from '../icons/iconMap'
import {
  Target, Flag, ChevronsUp, Check, Circle,
  LandPlot, Star, Banknote, Users, Trophy, Briefcase, type LucideIcon,
} from 'lucide-react'

const GOAL_TYPE_ICONS: Record<Goal['type'], LucideIcon> = {
  courts: LandPlot,
  reputation: Star,
  money: Banknote,
  members: Users,
  tournaments: Trophy,
  staff: Briefcase,
}

function GoalRow({ goal, progress, claimed, onClaim, highlighted = false, rowRef }: {
  goal: Goal
  progress: number
  claimed: boolean
  onClaim: () => void
  highlighted?: boolean
  rowRef?: (el: HTMLDivElement | null) => void
}) {
  const completed = progress >= goal.target
  const percent = Math.min(100, (progress / goal.target) * 100)
  const GoalIcon = GOAL_TYPE_ICONS[goal.type] ?? Target

  return (
    <div
      ref={rowRef}
      className="row-tile"
      style={{
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '0.55rem',
        opacity: claimed ? 0.7 : 1,
        transition: 'box-shadow 0.4s ease',
        ...(highlighted ? { boxShadow: '0 0 0 2px var(--gold), var(--glow-gold)' } : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
        <IconTile icon={GoalIcon} tone={claimed ? 'success' : completed ? 'cta' : 'accent'} />
        <div className="row-tile-main">
          <div className="row-tile-title">
            {goal.name}
            {claimed && <span className="pill pill-success"><Check size={10} /> Cobrado</span>}
          </div>
          <div className="row-tile-sub">{goal.description}</div>
        </div>
        <div className="row-tile-side">
          {!claimed && (
            <span className="money-positive" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {goal.reward.money ? `+${goal.reward.money.toLocaleString('es-ES')}€` : ''}
              {goal.reward.reputation ? `${goal.reward.money ? ' · ' : ''}+${goal.reward.reputation}★` : ''}
            </span>
          )}
          {completed && !claimed && (
            <AngledButton
              tone="gold"
              onClick={onClaim}
              style={{ animation: 'pulse 2s infinite' }}
            >
              Reclamar
            </AngledButton>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <div className="progress-track" style={{ flex: 1 }}>
          <div
            className={`progress-fill angled${completed ? ' success' : ''}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className="meta-text" style={{ minWidth: '58px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {goal.type === 'reputation' ? progress.toFixed(1) : Math.floor(progress)}/{goal.target}
        </span>
      </div>
    </div>
  )
}

// `compact` (used on the Dashboard) drops the header + the long milestones
// list, leaving just the next-level card and the weekly objectives so the
// Despacho stays focused; the full panel lives in Carrera → Metas.
export function GoalsTabContent({ compact = false }: { compact?: boolean } = {}) {
  const state = useGameStore()
  const { stats } = useAchievementsStore()
  const { claimGoal, addMoney, addReputation, claimedGoals, week } = state
  const [params, setParams] = useSearchParams()
  const goalParam = compact ? null : params.get('goal')
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const rowRefs = useRef(new Map<string, HTMLDivElement>())

  const handleClaim = (goal: Goal) => {
    if (goal.reward.money) addMoney(goal.reward.money, `Objetivo: ${goal.name}`)
    if (goal.reward.reputation) addReputation(goal.reward.reputation)
    claimGoal(goal.id)
  }

  // Deep-link landing (?goal=id from RewardAlert): scroll to the requested goal
  // (or the first claimable one if it no longer applies) and flash it. The param
  // is consumed when the flash ends — consuming it immediately would lose the
  // highlight to StrictMode's dev remount, whose second pass re-reads the URL.
  useEffect(() => {
    if (!goalParam) {
      setHighlightedId(null)
      return
    }
    const targetId = rowRefs.current.has(goalParam)
      ? goalParam
      : getFirstClaimableGoalId(useGameStore.getState(), stats, claimedGoals)
    const el = targetId ? rowRefs.current.get(targetId) : undefined
    if (el && targetId) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightedId(targetId)
    }
    const timer = window.setTimeout(() => {
      setHighlightedId(null)
      setParams(p => { p.delete('goal'); return p }, { replace: true })
    }, 2000)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalParam])

  const bindRow = (id: string) => (el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(id, el)
    else rowRefs.current.delete(id)
  }

  const unclaimedCount = countClaimableGoals(state, stats, claimedGoals)

  const nextLevel = getNextLevel(state.clubLevel)

  return (
    <div>
      {!compact && (
        <BroadcastHeader
          title="Metas del club"
          competition="Objetivos"
          icon={Target}
          meta={
            <>
              <span className="chip-angled tone-gold">Semana {week}</span>
              {unclaimedCount > 0 && <span className="chip-angled">{unclaimedCount} por reclamar</span>}
            </>
          }
        />
      )}

      {nextLevel && (
        <SectionDeck title="Siguiente nivel" icon={ChevronsUp} meta={`Nv.${nextLevel.level}: ${nextLevel.name}`}>
          <div className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <span className="icon-tile"><GameIcon domain="clubLevel" id={nextLevel.level} size={18} /></span>
              <div className="row-tile-main">
                <div className="row-tile-title">Nv.{nextLevel.level}: {nextLevel.name}</div>
                <div className="row-tile-sub">Cumple los requisitos para subir de categoría</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: '0.4rem' }}>
              {nextLevel.requirements.map((req, i) => {
                let current = 0
                switch (req.type) {
                  case 'courts': current = state.courts.length; break
                  case 'buildings': current = state.buildings.length; break
                  case 'staff': current = state.staff.length; break
                  case 'reputation': current = state.reputation; break
                  case 'services': current = state.enabledServices.length; break
                  case 'upgrades': current = Object.values(state.courtUpgrades).reduce((s, u) => s + u.length, 0); break
                  case 'members': current = Object.values(state.memberCounts).reduce((s, c) => s + c, 0); break
                }
                const met = current >= req.value
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    fontSize: '0.72rem', padding: '2px 0',
                    color: met ? 'var(--success)' : 'var(--text-secondary)',
                  }}>
                    {met ? <Check size={12} /> : <Circle size={10} />}
                    <span>{req.label}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {req.type === 'reputation' ? current.toFixed(1) : Math.floor(current)}/{req.value}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </SectionDeck>
      )}

      <SectionDeck
        title="Objetivos de la semana"
        icon={Target}
        meta={`Semana ${week}`}
        action={unclaimedCount > 0 ? <span className="pill pill-success">{unclaimedCount} por reclamar</span> : undefined}
      >
        <div className="space-y-2">
          {WEEKLY_GOALS.map(goal => (
            <GoalRow
              key={goal.id}
              goal={goal}
              progress={getGoalProgress(goal, state, stats)}
              claimed={claimedGoals.includes(goal.id)}
              onClaim={() => handleClaim(goal)}
              highlighted={highlightedId === goal.id}
              rowRef={bindRow(goal.id)}
            />
          ))}
        </div>
      </SectionDeck>

      {!compact && (
        <SectionDeck title="Hitos" icon={Flag}>
          {MILESTONE_GOALS.every(g => claimedGoals.includes(g.id)) ? (
            <EmptyStateRich
              icon={Trophy}
              title="Todos los hitos completados"
              sub="Tu club ya no tiene nada que demostrar... de momento"
            />
          ) : (
            <div className="space-y-2">
              {MILESTONE_GOALS
                .filter(g => !claimedGoals.includes(g.id))
                .map(goal => (
                  <GoalRow
                    key={goal.id}
                    goal={goal}
                    progress={getGoalProgress(goal, state, stats)}
                    claimed={false}
                    onClaim={() => handleClaim(goal)}
                    highlighted={highlightedId === goal.id}
                    rowRef={bindRow(goal.id)}
                  />
                ))}
            </div>
          )}
        </SectionDeck>
      )}
    </div>
  )
}
